import { useState, useEffect, useCallback } from 'react';
import { Modal, DialogModal } from '@/components';
import DOMPurify from 'dompurify';
import { formatTime } from '@/hooks/use-solver-timer';
import { toast } from 'react-toastify';
import { useMutationProvider } from '@/hooks';
import { useApi } from '@/api/useApi.tsx';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { OK_STATUS_CODE } from '@/constants';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import clsx from 'clsx';
import { useQueryClient } from 'react-query';
import { SolveQuestionRequest, ExpertResolveQuestionSolveRequest, ExpertResolveQuestionSolveRequestSolutionTypeEnum } from '@dopingtech/live_expert_bff_api_ts';

const WARNING_TIME_THRESHOLD = 180;

const VIDEO_LOADING_STATES = {
	IDLE: 'idle',
	LOADING: 'loading',
	LOADED: 'loaded',
	ERROR: 'error'
};

interface SolutionVideoModalProps {
	isOpen: boolean;
	onClose: () => void;
	onRetry?: () => void;
	recordedVideoData: {
		recordedData?: {
			url?: string;
			base64?: string;
		};
		blob?: Blob;
		url?: string;
		fromCamera?: boolean;
		filename?: string;
	} | null;
	activeQuestion: {
		questionId?: string;
		curriculumName?: string;
		curriculumElementUnitName?: string;
	};
	solverTimer: {
		isActive: boolean;
		remainingTime: number;
	};
	onSuccess?: () => void;
	onError?: (error: any) => void;
	showRetryButton?: boolean;
	isResolveMode?: boolean;
	resolveQueueId?: number | null;
}

const SolutionVideoModalV2 = ({
	isOpen,
	onClose,
	onRetry,
	recordedVideoData,
	activeQuestion,
	solverTimer,
	onSuccess,
	onError,
	showRetryButton = true,
	isResolveMode = false,
	resolveQueueId = null
}: SolutionVideoModalProps) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { SolverQuestionControllerApi, ExpertResolveQuestionControllerApi } = useApi();

	const [showRetryModal, setShowRetryModal] = useState(false);
	const [showDeleteVideoModal, setShowDeleteVideoModal] = useState(false);
	const [videoUrl, setVideoUrl] = useState(null);
	const [videoLoaded, setVideoLoaded] = useState(false);
	const [videoError, setVideoError] = useState(false);
	const [videoLoadingState, setVideoLoadingState] = useState(VIDEO_LOADING_STATES.IDLE);
	const [isUploading, setIsUploading] = useState(false);

	const resetAllStates = useCallback(() => {
		setShowRetryModal(false);
		setShowDeleteVideoModal(false);
		setVideoUrl(null);
		setVideoLoaded(false);
		setVideoError(false);
		setVideoLoadingState(VIDEO_LOADING_STATES.IDLE);
		setIsUploading(false);
	}, []);

	const solveQuestion = useMutationProvider((body: SolveQuestionRequest) => SolverQuestionControllerApi.solveQuestion(body), {
		onError: error => {
			ExceptionManager.errorHandler(error);
			onError?.(error);
			setIsUploading(false);
		},
		onSuccess: () => {
			toast.success('Video çözümün başarıyla gönderildi.');
			queryClient.refetchQueries({
				queryKey: [QUERY_KEYS.getRemainingPassedQuestionCount]
			});
			onSuccess?.();
			onClose();
		}
	});

	const solveResolveQuestion = useMutationProvider(
		(body: ExpertResolveQuestionSolveRequest) => ExpertResolveQuestionControllerApi.solveResolveQuestion(resolveQueueId!, body),
		{
			onError: error => {
				ExceptionManager.errorHandler(error);
				onError?.(error);
				setIsUploading(false);
			},
			onSuccess: () => {
				toast.success('Video çözümün başarıyla gönderildi.');
				onSuccess?.();
				onClose();
			}
		}
	);

	const handleVideoLoadStart = () => {
		if (isOpen) {
			setVideoLoadingState(VIDEO_LOADING_STATES.LOADING);
			setVideoLoaded(false);
			setVideoError(false);
		}
	};

	const handleVideoCanPlay = () => {
		if (isOpen) {
			setVideoLoadingState(VIDEO_LOADING_STATES.LOADED);
			setVideoLoaded(true);
			setVideoError(false);
		}
	};

	const handleVideoError = () => {
		if (videoLoadingState === VIDEO_LOADING_STATES.LOADING && isOpen) {
			setVideoLoadingState(VIDEO_LOADING_STATES.ERROR);
			setVideoError(true);
			setVideoLoaded(false);
			toast.error('Video oynatılırken bir hata oluştu. Yeniden çöz butonunu kullanarak soruyu yeniden çözebilirsiniz.', {
				toastId: 'video-error-toast'
			});
		}
	};

	const purify = dirty => ({
		__html: DOMPurify.sanitize(dirty)
	});

	const isSendButtonDisabled = () => {
		return isUploading || solveQuestion.isLoading || solveResolveQuestion.isLoading || !videoUrl || !videoLoaded || videoError;
	};

	const onSubmitVideo = async e => {
		e.preventDefault();

		if (!isOpen) return;

		if (!videoLoaded || videoError) {
			toast.error('Video oynatılamıyor! Lütfen yeniden deneyin.');
			return;
		}

		if (!activeQuestion?.questionId) {
			toast.error('Soru bilgisi bulunamadı!');
			return;
		}

		const videoElement = document.querySelector('.solution-video-modal video');
		if (videoElement) {
			videoElement.pause();
		}

		try {
			setIsUploading(true);

			const mimeType = 'video/mp4';
			let finalBlob;

			if (recordedVideoData.fromCamera) {
				if (recordedVideoData.recordedData?.url) {
					const videoResponse = await fetch(recordedVideoData.recordedData.url);
					finalBlob = await videoResponse.blob();
				} else {
					throw new Error('Kamera videosu için URL bulunamadı!');
				}
			} else {
				if (recordedVideoData.blob) {
					finalBlob = recordedVideoData.blob;
				} else if (recordedVideoData.url) {
					const videoResponse = await fetch(recordedVideoData.url);
					finalBlob = await videoResponse.blob();
				} else if (recordedVideoData.recordedData?.base64) {
					const base64Url = recordedVideoData.recordedData.base64.startsWith('data:')
						? recordedVideoData.recordedData.base64
						: `data:${mimeType};base64,${recordedVideoData.recordedData.base64}`;

					const base64Data = base64Url.split(',')[1];
					const binaryString = atob(base64Data);
					const bytes = new Uint8Array(binaryString.length);

					for (let i = 0; i < binaryString.length; i++) {
						bytes[i] = binaryString.charCodeAt(i);
					}

					finalBlob = new Blob([bytes], { type: mimeType });
				} else {
					throw new Error("Video blob'u bulunamadı!");
				}
			}

			const videoSize = parseFloat((finalBlob.size / (1024 * 1024)).toFixed(2));

			const getVideoDuration = () => {
				return new Promise(resolve => {
					const video = document.createElement('video');
					video.preload = 'metadata';
					video.muted = true;

					const blobUrl = URL.createObjectURL(finalBlob);

					video.onloadedmetadata = () => {
						const duration = Math.round(video.duration || 0);
						URL.revokeObjectURL(blobUrl);
						video.src = '';
						resolve(duration);
					};

					video.onerror = () => {
						URL.revokeObjectURL(blobUrl);
						video.src = '';
						resolve(0);
					};

					setTimeout(() => {
						URL.revokeObjectURL(blobUrl);
						video.src = '';
						resolve(0);
					}, 3000);

					video.src = blobUrl;
				});
			};

			const videoDuration = await getVideoDuration();

			const uploadUrlResponse = await SolverQuestionControllerApi.getUploadUrl('video/mp4', videoSize, videoDuration);

			const uploadUrl = uploadUrlResponse.data.data;

			if (!uploadUrl) {
				throw new Error('Upload URL alınamadı!');
			}

			const uploadResponse = await fetch(uploadUrl.url, {
				method: 'PUT',
				body: finalBlob,
				headers: {
					'Content-Type': mimeType,
					'Content-Length': finalBlob.size.toString()
				}
			});

			if (uploadResponse?.status !== OK_STATUS_CODE) {
				throw new Error('Video yükleme başarısız!');
			}

			if (isResolveMode && resolveQueueId) {
				await solveResolveQuestion.mutateAsync({
					solutionVideo: uploadUrl.fileName,
					solutionType: ExpertResolveQuestionSolveRequestSolutionTypeEnum.Expert
				});
			} else {
				await solveQuestion.mutateAsync({
					solutionVideo: uploadUrl.fileName,
					questionId: activeQuestion.questionId
				});
			}
		} catch (error) {
			console.error('Video gönderme hatası:', error);
			toast.error('Video gönderilirken hata oluştu!');
			setIsUploading(false);
		}
	};

	const handleRetryClick = () => {
		setShowRetryModal(true);
	};

	const handleConfirmRetry = () => {
		setShowRetryModal(false);
		setVideoLoaded(false);
		setVideoError(false);
		setVideoLoadingState(VIDEO_LOADING_STATES.IDLE);
		setVideoUrl(null);
		setIsUploading(false);
		onRetry?.();
	};

	const onCloseSolutionVideoModal = () => {
		setShowDeleteVideoModal(true);
	};

	const handleDeleteVideo = () => {
		setShowDeleteVideoModal(false);
		resetAllStates();
		onClose?.();
		if (location.pathname !== NavigationPath.solverSolve) {
			navigate(NavigationPath.solverSolve);
		}
	};

	useEffect(() => {
		return () => {
			if (videoUrl && videoUrl.startsWith('blob:')) {
				URL.revokeObjectURL(videoUrl);
			}
		};
	}, []);

	useEffect(() => {
		if (isOpen) {
			setShowRetryModal(false);
			setShowDeleteVideoModal(false);
			setVideoLoaded(false);
			setVideoError(false);
			setVideoLoadingState(VIDEO_LOADING_STATES.IDLE);
		} else {
			resetAllStates();
		}
	}, [isOpen, resetAllStates]);

	useEffect(() => {
		if (!recordedVideoData || !isOpen) {
			return;
		}

		if (videoUrl && videoUrl.startsWith('blob:')) {
			URL.revokeObjectURL(videoUrl);
		}

		if (recordedVideoData?.blob) {
			try {
				const blobUrl = URL.createObjectURL(recordedVideoData.blob);
				setVideoUrl(blobUrl);
			} catch (error) {
				console.error('Error creating blob URL:', error);
				setVideoUrl(null);
			}
			return;
		}

		if (recordedVideoData?.url) {
			setVideoUrl(recordedVideoData.url);
			return;
		}

		if (recordedVideoData?.recordedData?.url) {
			setVideoUrl(recordedVideoData.recordedData.url);
			return;
		}

		if (recordedVideoData?.recordedData?.base64) {
			try {
				const base64Data = recordedVideoData.recordedData.base64;
				const base64String = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;

				const binaryString = atob(base64String);
				const bytes = new Uint8Array(binaryString.length);

				for (let i = 0; i < binaryString.length; i++) {
					bytes[i] = binaryString.charCodeAt(i);
				}

				const blob = new Blob([bytes], { type: 'video/mp4' });
				const blobUrl = URL.createObjectURL(blob);
				setVideoUrl(blobUrl);
			} catch (error) {
				console.error('Error processing base64:', error);
				setVideoUrl(null);
			}
			return;
		}

		setVideoUrl(null);
	}, [recordedVideoData, isOpen]);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onCloseSolutionVideoModal}
				contentClassName="solution-video-modal"
				showCloseButton
				size="lg"
				variant="centered"
				closeOnClickOutside={false}
				customXIcon={<i className="icons-x" />}>
				{!isResolveMode && solverTimer.isActive && (
					<div className="modal-timer">
						<p>
							Kalan Süre:
							<b
								style={{
									color: solverTimer.remainingTime <= WARNING_TIME_THRESHOLD ? 'var(--danger-text)' : 'inherit'
								}}>
								{formatTime(solverTimer.remainingTime)}
							</b>
						</p>
					</div>
				)}

				<div className="modal-header">
					<h2>{recordedVideoData?.fromCamera ? 'Kamera Kaydını Kontrol Et' : 'Çözüm Videosunu Kontrol Et'}</h2>
					<p className="modal-description">Aşağıdaki video çözümünüzü kontrol edin ve gönderin.</p>
				</div>

				<form onSubmit={onSubmitVideo}>
					<div className="modal-body">
						<div className="video-preview">
							{videoUrl ? (
								<video
									key={videoUrl}
									src={videoUrl}
									controls
									autoPlay={true}
									preload="metadata"
									onLoadStart={handleVideoLoadStart}
									onCanPlay={handleVideoCanPlay}
									onError={handleVideoError}
								/>
							) : (
								<div className="video-loader-wrapper">
									<p>Video Hazırlanıyor...</p>
								</div>
							)}
						</div>

						<div className="form-section">
							<div className="form-header">
								<div>
									<span className="grade-badge">{activeQuestion?.curriculumName}</span>
									<h3>{activeQuestion?.curriculumElementUnitName}</h3>
								</div>

								{showRetryButton && (
									<button
										type="button"
										className="btn-retry"
										onClick={handleRetryClick}
										disabled={(!isResolveMode && !solverTimer.isActive) || isUploading}>
										<div className="retry-icon" />
										{recordedVideoData?.fromCamera ? 'Tekrar Çek' : 'Yeniden Çöz'}
									</button>
								)}
							</div>
						</div>
					</div>

					<div className="modal-footer">
						<button
							type="submit"
							className={clsx('btn-submit', {
								disabled: isSendButtonDisabled(),
								error: videoError
							})}
							disabled={isSendButtonDisabled()}>
							Videoyu Gönder
						</button>
					</div>
				</form>
			</Modal>

			<DialogModal
				isOpen={showRetryModal}
				onClose={() => setShowRetryModal(false)}
				className="solver-dialog-modal-retry"
				variant="warning"
				title="Emin Misin?"
				primaryButtonText={recordedVideoData?.fromCamera ? 'Tekrar Çek' : 'Yeniden Çöz'}
				secondaryButtonText="Vazgeç"
				onSecondaryClick={() => setShowRetryModal(false)}
				onPrimaryClick={handleConfirmRetry}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify('Videoyu kalan süre içerisinde yeniden çözmek istediğine emin misin?')} />
			</DialogModal>

			<DialogModal
				isOpen={showDeleteVideoModal}
				onClose={() => setShowDeleteVideoModal(false)}
				className="solver-dialog-delete-video-modal"
				variant="trash"
				title="Videoyu Sil"
				primaryButtonText="Evet, Sil"
				secondaryButtonText="Vazgeç"
				onSecondaryClick={() => setShowDeleteVideoModal(false)}
				onPrimaryClick={handleDeleteVideo}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify('Eğer sayfayı kapatırsan video silinecek. Bu işlem geri alınamaz. Videoyu silmek istediğine emin misin?')} />
			</DialogModal>

			{isUploading && (
				<div className="uploading-overlay">
					<div className="uploading-content">
						<div className="spinner"></div>
						<h1>Videonuz yükleniyor...</h1>
						<p>Bu işlem birkaç saniye sürebilir. Lütfen bu sayfadan ayrılmadan yüklemenin tamamlanmasını bekleyin. </p>
					</div>
				</div>
			)}
		</>
	);
};

export default SolutionVideoModalV2;
